import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
// import { db, auth } from "../firebase-config";
import {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
  useContext,
} from "react";
import { useBlazeQueryMutate, useLectureRecommendationMutate } from "./blaze";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import { useAuth } from "../../../context/AuthContext";
import { useRouter } from "next/router";
import { UserContext } from "../../../context";
import { getClassName } from "../../../database/story";
import { NextResponse } from "next/server";
// import { useLectureHistory } from "./lecture-history";

// Extend Window interface to include our custom tool execution function
declare global {
  interface Window {
    executeAssistantTool?: (
      command: string,
      args: Record<string, string>
    ) => Promise<any>;
  }
}

// Sample lecture IDs for demonstrating the feature
const sampleLectureIds = [
  "class_9_learn_science_chemistry_matterinoursurroundings_chapter_canmatterchangeitsstate",
  "class_9_learn_science_chemistry_matterinoursurroundings_chapter_introduction",
  "class_9_learn_science_chemistry_matterinoursurroundings_quickreview_revision",
  "class_9_learn_maths_linearequationsintwovariables_chapter_introduction",
  "class_9_learn_englishgrammar_basicgrammar_adjectives_chapter_introduction",
  "class_9_learn_englishgrammar_basicgrammar_adjectives_chapter_kindsofadjectives",
];

// Hook to fetch and manage lecture history
export const useLectureHistory = () => {
  const [lectureHistory, setLectureHistory] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Fetch lecture history from the API
  const fetchLectureHistory = useCallback(
    async (lectureIds = sampleLectureIds) => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await axios.post("/api/batch", {
          ids: lectureIds,
        });

        if (response.data && response.data.documents) {
          setLectureHistory(response.data.documents);
          console.log(
            `Fetched ${response.data.documents.length} lecture history items`
          );
        } else {
          console.warn(
            "Lecture history API returned empty or invalid data structure"
          );
          // Set empty array as a fallback
          setLectureHistory([]);
        }
      } catch (err) {
        console.error("Error fetching lecture history:", err);
        // Important: Set an empty array instead of leaving previous state
        // This ensures we don't block the chat functionality
        setLectureHistory([]);
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to fetch lecture history")
        );
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // Fetch lecture history on mount
  useEffect(() => {
    fetchLectureHistory();
  }, [fetchLectureHistory]);

  return {
    // Always return an array, even if there's an error
    lectureHistory: lectureHistory || [],
    isLoading,
    error,
    refetchLectureHistory: fetchLectureHistory,
  };
};

export interface Message {
  id: string;
  text: string;
  sender: "user" | "assistant";
  timestamp: any;
  metadata?: {
    isStreaming?: boolean;
    streamComplete?: boolean;
    response_type?: string;
    isSearching?: boolean;
    processingTool?: boolean;
    detected_subject?: string;
    detected_chapter?: string;
    detected_language?: string;
    error_type?: string;
    original_error?: string;
    isCancelled?: boolean;
    topic_query_text?: string;
    hinglish_answer?: string;
    english_answer?: string;
    suggested_follow_ups?: string[];
    learning_path?: any;
    suggested_lectures?: {
      lecture_title: string;
      lecture_type: string;
      lecture_path: string;
      description: string;
      youtube_url?: string;
    }[];
    metrics?: any;
    tool_call?: string;
    isNavigation?: boolean;
    navigationTarget?: string;
    navigationLabel?: string;
    navigationMessage?: string;
    show_feedback_ui?: boolean;
    selectedBuddyId?: string | null;
    session_request_data?: {
      category_name?: string;
      subject_name?: string;
      chapter_name?: string;
      initial_doubt_description?: string;
      grade?: string;
    };
    live_classes?: Array<{
      id: string;
      title: string;
      subject: string;
      topic: string;
      instructor: string;
      start_time: string;
      duration: number;
      status: string;
      meeting_link: string;
    }>;
    blaze_sessions?: Array<{
      id: string;
      title: string;
      subject: string;
      topic: string;
      student_id: string;
      tutor_id: string | null;
      tutor_name: string;
      status: string;
      created_at: string;
      completed_at: string | null;
      grade: string;
      score: number;
      detail_url: string;
      subject_color_gradient?: string[] | null;
      student_name?: string | null;
      student_profile_pic?: string | null;
      instructor_profile_pic?: string | null;
      skill?: string | null;
      topic_cms_path?: string | null;
    }>;
    recommended_buddy?: string;
    buddy_message?: string;
    buddy_explanation?: string;
    buddy_suggestion?: string;
    switch_prompt?: string;
    query_classification?: string;
    current_buddy?: string;
    buddy_used?: string;
    [key: string]: any;
  };
  messageType?: string;
  content?: string;
  response_type?:
    | "academic_within_scope"
    | "academic_out_of_scope"
    | "learning_path"
    | "lecture_suggestion"
    | "live_classes_schedule"
    | "blaze_sessions_list"
    | "buddy_recommendation";
  suggested_lectures?: {
    lecture_title: string;
    lecture_type: string;
    lecture_path: string;
    description: string;
    youtube_url?: string;
  }[];
  resources?: {
    title: string;
    url: string;
    type: string;
    description?: string;
    thumbnail?: string;
  }[];
  sources?: {
    title: string;
    url: string;
    snippet?: string;
  }[];
  suggested_follow_ups?: string[];
  learning_path?: any;
  detected_subject?: string;
  detected_chapter?: string;
  detected_grade?: string;
  recommended_buddy?: string;
  buddy_message?: string;
  buddy_explanation?: string;
  buddy_suggestion?: string;
  switch_prompt?: string;
  query_classification?: string;
  current_buddy?: string;
  buddy_used?: string;
  live_classes?: Array<{
    id: string;
    title: string;
    subject: string;
    topic: string;
    instructor: string;
    start_time: string;
    duration: number;
    status: string;
    meeting_link: string;
  }>;
  blaze_sessions?: Array<{
    id: string;
    title: string;
    subject: string;
    topic: string;
    student_id: string;
    tutor_id: string | null;
    tutor_name: string;
    status: string;
    created_at: string;
    completed_at: string | null;
    grade: string;
    score: number;
    detail_url: string;
    subject_color_gradient?: string[] | null;
    student_name?: string | null;
    student_profile_pic?: string | null;
    instructor_profile_pic?: string | null;
    skill?: string | null;
    topic_cms_path?: string | null;
  }>;
  tool_calls?: {
    name: string;
    arguments: Record<string, any>;
  }[];
  tool_execution_results?: any;
  tool_execution_error?: string;
  show_feedback_ui?: boolean;
  session_request_data?: {
    category_name?: string;
    subject_name?: string;
    chapter_name?: string;
    initial_doubt_description?: string;
    grade?: string;
  };
}

export const useAssistantChat = () => {
  const [userId, setUserId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string>("");
  const [messages, setMessages] = useState<Message[]>([]);
  const queryClient = useQueryClient();
  const abortControllerRef = useRef<AbortController | null>(null);
  const [requestCancelled, setRequestCancelled] = useState(false);
  const [lastQuery, setLastQuery] = useState<string>("");
  const [fetchStartTime, setFetchStartTime] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [conversationHistory, setConversationHistory] = useState<
    Array<{ role: string; function_calls?: any; content: string }>
  >([]);
  const [isRecommendationsLoading, setIsRecommendationsLoading] =
    useState(false);

  const userContext = useContext(UserContext);
  const [user] = userContext?.user || [null];
  const [isUserProTier] = userContext?.tier || [false];

  // Fetch lecture history using our custom hook
  const {
    lectureHistory,
    isLoading: isLectureHistoryLoading,
    error: lectureHistoryError,
  } = useLectureHistory();

  // Log any errors with lecture history, but don't block chat functionality
  useEffect(() => {
    if (lectureHistoryError) {
      console.error(
        "Lecture history error detected:",
        lectureHistoryError.message
      );
      console.log("Chat will continue without lecture history context");
    }
  }, [lectureHistoryError]);

  // OpenAI query mutation
  const blazeQuery = useBlazeQueryMutate();

  // Lecture recommendation mutation
  const {
    mutateAsync: lectureRecommendationMutate,
    isPending: lectureRecommendationLoading,
    reset: lectureRecommendationReset,
  } = useLectureRecommendationMutate();

  // Listen for auth state changes to get the current user ID
  useEffect(() => {
    const unsubscribe =
      require("../../../firebase-config").auth.onAuthStateChanged(
        (user: any) => {
          setUserId(user?.uid || null);
        }
      );
    return () => unsubscribe();
  }, []);

  // Create a session ID for this browser session
  useEffect(() => {
    // If user is logged in, use their ID as part of the session
    const userPrefix = userId || "guest";
    // Create a unique session ID for this browser session
    const browserSessionId = `${userPrefix}_${Date.now()}_${Math.random()
      .toString(36)
      .substring(2, 9)}`;

    setSessionId(browserSessionId);
  }, [userId]);

  // Function to interrupt ongoing requests
  const interruptRequest = () => {
    // Don't check for all cancellation messages - only check if there's already
    // a streaming placeholder message that we need to update
    const streamingMessage = messages.find(
      (msg) => msg.metadata?.isStreaming === true
    );

    // Abort any ongoing fetch requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Cancel any tanstack query mutations
    blazeQuery.reset();
    lectureRecommendationReset();

    // Set the cancellation flag
    setRequestCancelled(true);

    // If we're in the middle of getting the OpenAI response (no last assistant message yet)
    // add a cancellation message to the chat
    if (blazeQuery.isPending && messages.length > 0) {
      // If there's a streaming message, update it instead of creating a new one
      if (streamingMessage) {
        console.log("Line 198 | Cancelling request");
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === streamingMessage.id
              ? {
                  ...msg,
                  text: "I've stopped processing your request as requested.",
                  messageType: "cancellation",
                  metadata: {
                    isCancelled: true,
                    error_type: "user_cancelled",
                    streamComplete: true,
                    isStreaming: false,
                    function_calls: msg.metadata?.function_calls,
                  },
                }
              : msg
          )
        );
      } else {
        // Find the last message from the user
        const lastMessageIsUser =
          messages[messages.length - 1].sender === "user";

        // Only add a message if the last message was from the user and there's no streaming message
        if (lastMessageIsUser) {
          console.log("Line 223 | Cancelling request");
          const cancelMessage: Message = {
            id: `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
            text: "This request was cancelled by user",
            sender: "assistant",
            timestamp: new Date().toISOString(),
            messageType: "cancellation",
            metadata: {
              isCancelled: true,
              streamComplete: true,
              isStreaming: false,
              error_type: "user_cancelled",
            },
          };

          setMessages((prev) => [...prev, cancelMessage]);
        }
      }
    }
  };

  // Function to retry fetching resources - memoized with useCallback
  const retryResourceFetch = useCallback(async () => {
    if (!lastQuery) return;

    // Reset request cancelled state
    setRequestCancelled(false);

    // Get the last assistant message
    const lastAssistantMessage = messages
      .filter((msg) => msg.sender === "assistant")
      .pop();

    if (!lastAssistantMessage) return;

    // Get the english answer from the last message
    const englishAnswer =
      lastAssistantMessage.metadata?.english_answer ||
      lastAssistantMessage.text ||
      "";

    // Set loading state to true at the start of the retry
    setIsRecommendationsLoading(true);

    try {
      // Create a new AbortController for this request
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      // Fetch recommendations based on the last query and answer
      const recommendationResponse = await lectureRecommendationMutate({
        query: {
          question: lastQuery,
          answer: englishAnswer,
        },
        signal,
      });

      // If the request was aborted, don't continue processing
      if (signal.aborted) return;

      // Extract recommendations
      const recommendations = recommendationResponse.data.results.map(
        (result) => ({
          id: result.id,
          title: result.title,
          path: result.path,
          youtube_url: result.youtube_url,
          score: result.score,
          type: result.type,
        })
      );

      // Create resources from recommendations for the ResourcesSection component
      // Convert the top recommendations into resources format
      const resources = recommendations
        .filter((rec) => rec.score && rec.score > 0.7) // Only use high-quality matches
        .slice(0, 5) // Limit to 5 top resources
        .map((rec) => ({
          title: rec.title,
          url: `/auth_classroom?subject=${rec.id
            .split("_")
            .pop()}&chapter=${rec.path.split("/").pop()}`,
          type: rec.type || "video", // Default to video if type is not available
          description: `From ${rec.id.split("_")[3] || "PuStack"} course`,
          thumbnail: rec.youtube_url
            ? `https://img.youtube.com/vi/${getYouTubeVideoId(
                rec.youtube_url
              )}/mqdefault.jpg`
            : undefined,
        }));

      // Get additional web resources if appropriate
      if (
        lastAssistantMessage.metadata?.response_type ===
          "academic_within_scope" &&
        resources.length < 3
      ) {
        try {
          // This would be a call to an external API to get web resources
          // For this example, we'll add some sample web resources
          const webResources = [
            {
              title:
                "Understanding " + lastQuery.split(" ").slice(0, 3).join(" "),
              url:
                "https://pustack.com/resources/" +
                lastQuery.split(" ")[0].toLowerCase(),
              type: "article",
              description:
                "Explore this topic in depth with our comprehensive guide",
              thumbnail: undefined,
            },
            {
              title: "Interactive Examples: " + lastQuery.split(" ")[0],
              url:
                "https://pustack.com/interactive/" +
                lastQuery.split(" ")[0].toLowerCase(),
              type: "tool",
              description: "Practice with interactive examples",
              thumbnail: undefined,
            },
          ];

          // Add these to our resources
          resources.push(...webResources);
        } catch (error) {
          console.error("Error fetching web resources:", error);
        }
      }

      // Fetch relevant live classes (past, present, future) for the retry request
      const fetchLiveClasses = async () => {
        try {
          // Create abort controller for live classes fetch
          if (!abortControllerRef.current) {
            abortControllerRef.current = new AbortController();
          }
          const signal = abortControllerRef.current.signal;

          // Extract subject from recommendations or message metadata
          const detectedSubject =
            lastAssistantMessage.metadata?.detected_subject ||
            (recommendations.length > 0
              ? recommendations[0].id.split("_").pop()
              : null);

          // Get user grade and pro status from UserContext instead of localStorage
          const userGrade = user?.grade || "class_9";

          // Use actual API endpoint instead of mock data
          const response = await axios.post(
            "/api/live-classes",
            {
              grade: `class_${userGrade}`,
              is_pro_user: isUserProTier,
              filters: {
                // subject_filter: detectedSubject,
                query: lastQuery, // Use the user's query for semantic search
                // Don't set time filters to get both upcoming and past classes
              },
            },
            { signal }
          );

          // Return the live classes from the API response
          return response.data.live_classes || [];
        } catch (error) {
          // Check if this is an abort error
          if (axios.isCancel(error)) {
            console.log("Live classes fetch aborted");
            return [];
          }
          console.error("Error fetching live classes:", error);
          return [];
        }
      };

      // Fetch Blaze sessions relevant to the user's query
      const fetchBlazeSessions = async () => {
        try {
          // Use the same abort controller
          if (!abortControllerRef.current) {
            abortControllerRef.current = new AbortController();
          }
          const signal = abortControllerRef.current.signal;

          // Get user ID for fetching student sessions
          const userId = user?.uid || null;

          // Don't fetch if no user is logged in
          if (!userId) {
            console.log("No user ID available, skipping Blaze sessions fetch");
            return [];
          }

          // Call the Blaze sessions API endpoint
          const response = await axios.post(
            "https://vector-search-python-fast-661886367826.asia-south1.run.app/search-blaze-sessions", // Use the correct endpoint
            {
              student_id: userId, // REQUIRED: The student's ID
              query: lastQuery, // The semantic search query
              filters: {
                // Optional filters can be added here
                // skill: lastAssistantMessage.metadata?.detected_subject,
              },
              size: 4, // Limit to 4 sessions
              min_score: 0.675, // Default minimum score threshold
            },
            { signal }
          );

          console.log("Fetched Blaze sessions:", response.data);
          return response.data.results || [];
        } catch (error) {
          // Check if this is an abort error
          if (axios.isCancel(error)) {
            console.log("Blaze sessions fetch aborted");
            return [];
          }
          console.error("Error fetching Blaze sessions:", error);
          return [];
        }
      };

      // Fetch live classes and update the message with all resources
      const liveClasses = await fetchLiveClasses();

      // Fetch Blaze sessions if user is logged in
      const blazeSessions = await fetchBlazeSessions();

      // Update the assistant message with recommendations and resources
      const realIndex =
        messages.length -
        1 -
        (messages.length -
          1 -
          messages.findIndex((msg) => msg.id === lastAssistantMessage.id));
      setMessages((prev) =>
        prev.map((msg, index) =>
          index === realIndex
            ? {
                ...msg,
                metadata: {
                  ...msg.metadata,
                  recommendations,
                },
                resources: resources.length > 0 ? resources : undefined,
                live_classes: liveClasses.length > 0 ? liveClasses : undefined,
                blaze_sessions:
                  blazeSessions.length > 0 ? blazeSessions : undefined,
              }
            : msg
        )
      );

      // Set loading state to false after successful completion
      setIsRecommendationsLoading(false);
    } catch (error) {
      console.error("Error refetching recommendations:", error);
      // Set loading state to false in case of error
      setIsRecommendationsLoading(false);
    }
  }, [
    messages,
    setMessages,
    lectureRecommendationMutate,
    lastQuery,
    setRequestCancelled,
    abortControllerRef,
    user,
    isUserProTier,
  ]);

  // Helper function to extract YouTube video ID
  const getYouTubeVideoId = (url: string): string | null => {
    if (!url) return null;
    if (url.includes("youtu.be/")) {
      const id = url.split("youtu.be/")[1]?.split(/[?&]/)[0];
      return id || null;
    }
    if (url.includes("youtube.com/watch")) {
      const urlParams = new URLSearchParams(url.split("?")[1]);
      return urlParams.get("v");
    }
    if (url.includes("youtube.com/embed/")) {
      return url.split("youtube.com/embed/")[1]?.split(/[?&]/)[0] || null;
    }
    return null;
  };

  // Add a new user message
  const addMessage = useMutation({
    mutationFn: async (
      input: string | { text: string; selectedBuddyId?: string | null }
    ) => {
      // Extract text and selectedBuddyId from input
      let text: string;
      let selectedBuddyId: string | null = null;

      if (typeof input === "string") {
        text = input;
      } else {
        text = input.text;
        selectedBuddyId = input.selectedBuddyId ?? null;
      }

      // Create a new message
      const newMessage: Message = {
        id: `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        text: text,
        sender: "user",
        timestamp: new Date(),
      };

      // Add to local state
      setMessages((prevMessages) => [...prevMessages, newMessage]);

      // Process the message with AI
      processUserMessage(text, selectedBuddyId);

      return { success: true };
    },
  });

  // Define a custom error interface
  interface CustomError extends Error {
    timeout?: boolean;
  }

  // Process user message with OpenAI and get recommendations
  const processUserMessage = async (
    query: string,
    selectedBuddyId: string | null = null
  ) => {
    console.log("🚀 [PROCESS_USER_MESSAGE] Called with query:", query);
    console.log("🤖 [PROCESS_USER_MESSAGE] selectedBuddyId:", selectedBuddyId);
    console.log(
      "📊 [PROCESS_USER_MESSAGE] Current messages count:",
      messages.length
    );
    console.log(
      "🔍 [PROCESS_USER_MESSAGE] Call stack:",
      new Error().stack?.split("\n").slice(1, 4)
    );

    setRequestCancelled(false);

    // Create a placeholder message ID at the beginning of the function
    const assistantPlaceholderId = `${Date.now()}_${Math.random()
      .toString(36)
      .substring(2, 9)}`;

    console.log("Processing the message 503 | ");

    // Extract and process tool calls
    const processToolCalls = async (text: string) => {
      console.log("Processing tool calls for text:", text);

      // Pattern to match both formats:
      // 1. !tool[[ToolName:param1=value1,param2=value2]]
      // 2. !tool[[ToolName:destination=/value]]
      const toolPattern = /!tool\[\[(.*?):(.*?)\]\]/g;
      let match;
      let modifiedText = text;

      while ((match = toolPattern.exec(text)) !== null) {
        const fullMatch = match[0];
        const toolName = match[1];
        const paramsString = match[2];

        console.log(`Detected tool: ${toolName}, params: ${paramsString}`);

        // Handle Maps_to_page tool specially
        if (toolName === "Maps_to_page") {
          console.log(
            "🎯 MAPS_TO_PAGE TOOL DETECTED! Will create a navigation bubble."
          );

          // Extract destination - handle both formats
          let destination = "Home";
          let label;
          let navigationMessage;

          if (paramsString) {
            const params = paramsString.split(",").reduce((acc, param) => {
              const equalsIndex = param.indexOf("=");
              if (equalsIndex > 0) {
                const key = param.substring(0, equalsIndex).trim();
                const value = param.substring(equalsIndex + 1).trim();
                if (key && value !== undefined) {
                  acc[key] = value;
                }
              }
              return acc;
            }, {} as Record<string, string>);

            destination = params.destination || "Home";
            navigationMessage = params.message || "";
          } else {
            // If it's just a plain path like "/blaze"
            destination = paramsString.replace(/^\//, ""); // Remove leading slash if present
          }

          // Generate a nice label based on destination
          switch (destination.toLowerCase()) {
            case "blaze":
            case "/blaze":
            case "blazeaichat":
              label = "Go to Blaze AI Chat";
              destination = "blaze";
              break;
            case "classes":
            case "/classes":
            case "classroom":
              label = "Explore Classes";
              destination = "classes";
              break;
            default:
              label = "Return to Home";
              destination = "home";
          }

          // Set message metadata for navigation
          setMessages((prev) => {
            if (!prev) return prev;
            return prev.map((msg) =>
              msg.id === assistantPlaceholderId
                ? {
                    ...msg,
                    metadata: {
                      ...msg.metadata,
                      isNavigation: true,
                      navigationTarget: destination,
                      navigationLabel: label,
                      navigationMessage: navigationMessage,
                    },
                  }
                : msg
            );
          });

          // Remove the tool call from the displayed text
          modifiedText = modifiedText.replace(fullMatch, "");
        } else {
          // Process other tool calls as before
          // Parse parameters - Define params outside try/catch so it's available in both blocks
          const params: Record<string, string> = {};

          if (paramsString.includes("=")) {
            // Key-value format
            paramsString.split(",").forEach((pair) => {
              const [key, value] = pair.split("=").map((s) => s.trim());
              if (key && value) {
                params[key] = value;
              }
            });
          }

          try {
            // Execute the tool
            const result = await window.executeAssistantTool?.(
              toolName.trim(),
              params
            );

            // Create proper function call with result for conversation history
            const functionCallWithResult = {
              name: toolName.trim(),
              args: params,
              result: result,
            };

            // Update message with result - using assistantPlaceholderId
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantPlaceholderId
                  ? {
                      ...msg,
                      tool_execution_results: result,
                      metadata: {
                        ...msg.metadata,
                        processingTool: false,
                        // Update the function_calls with the result
                        function_calls: [functionCallWithResult],
                      },
                    }
                  : msg
              )
            );

            // Update conversation history immediately
            setTimeout(() => {
              const updatedHistory = formatMessagesForHistory(messages);
              console.log(
                "Updating conversation history after tool execution:",
                JSON.stringify(updatedHistory, null, 2)
              );
              setConversationHistory(updatedHistory);
            }, 100);
          } catch (error) {
            console.error("Error executing tool:", error);

            // Create proper function call with error for conversation history
            const functionCallWithError = {
              name: toolName.trim(),
              args: params,
              error: String(error),
            };

            // Update message with error - using assistantPlaceholderId
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantPlaceholderId
                  ? {
                      ...msg,
                      tool_execution_error: String(error),
                      metadata: {
                        ...msg.metadata,
                        processingTool: false,
                        // Update the function_calls with the error
                        function_calls: [functionCallWithError],
                      },
                    }
                  : msg
              )
            );

            // Update conversation history immediately with the error
            setTimeout(() => {
              const updatedHistory = formatMessagesForHistory(messages);
              console.log(
                "Updating conversation history after tool error:",
                JSON.stringify(updatedHistory, null, 2)
              );
              setConversationHistory(updatedHistory);
            }, 100);
          }
        }
      }

      return modifiedText.trim();
    };

    // Clone current conversation history for this request
    const currentHistory = [...conversationHistory];

    // Start streaming

    // Add a timeout mechanism
    let timeoutId: NodeJS.Timeout | null = null;

    try {
      // Create a new AbortController for this request
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      // Set up a 60-second timeout for the request (adjust as needed)
      timeoutId = setTimeout(() => {
        if (abortControllerRef.current) {
          console.log("Request timed out, aborting");
          // Create a custom abort error with a timeout flag
          const timeoutError = new Error("Request timed out") as CustomError;
          timeoutError.name = "AbortError";
          timeoutError.timeout = true;

          // Store the error for reference
          const customError = timeoutError;

          // Abort the controller
          abortControllerRef.current.abort();

          // Handle timeout specifically
          const timeoutMessage: Message = {
            id: `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
            text: "I'm sorry, but your request is taking longer than expected. Please try again with a simpler question or try later when our servers are less busy.",
            sender: "assistant",
            timestamp: new Date().toISOString(),
            metadata: {
              error_type: "timeout",
              original_error: "Request timed out after 60 seconds",
              isStreaming: false,
              selectedBuddyId: selectedBuddyId,
            },
          };
          setMessages((prev) => [...prev, timeoutMessage]);
        }
      }, 2 * 60 * 1000); // 2 minutes timeout

      console.log("Processing the message 684 | ");
      // Add a placeholder message that will be updated with chunks
      const placeholderMessage: Message = {
        id: assistantPlaceholderId, // Use the ID defined at the top of the function
        text: "...", // This will be updated as we receive chunks
        sender: "assistant",
        timestamp: new Date().toISOString(),
        metadata: {
          isStreaming: true,
          selectedBuddyId: selectedBuddyId,
        },
      };

      // Add the placeholder message to the state
      setMessages((prev) => [...prev, placeholderMessage]);

      // Stream callbacks to handle the streaming response
      const streamCallbacks = {
        onInterimMessage: (interimMessage: {
          message: string;
          topic_query_text: string;
          response_type: string;
        }) => {
          console.log("🔄 Interim message received:", interimMessage);
          // Update the message with the interim response
          // Don't replace the message text if we're processing a tool call
          setMessages((prev) =>
            prev.map((msg) => {
              if (
                msg.id === assistantPlaceholderId &&
                !msg.metadata?.processingTool
              ) {
                // Preserve any tool_call in the message text
                const currentText = msg.text || "";
                const hasToolCall = /!tool\[\[(.*?)\]\]/g.test(currentText);
                const newText = hasToolCall
                  ? currentText // Keep existing text if it has a tool call
                  : interimMessage.message;

                console.log("📝 Updating interim message:", {
                  currentText,
                  newText,
                  hasToolCall,
                });

                return {
                  ...msg,
                  text: newText,
                  metadata: {
                    ...msg.metadata,
                    isSearching: true,
                    isStreaming: false,
                    streamComplete: false,
                    response_type: interimMessage.response_type,
                    topic_query_text: interimMessage.topic_query_text,
                    answer: newText,
                    english_answer: newText,
                    hinglish_answer: newText,
                  },
                };
              }
              return msg;
            })
          );
        },

        onChunkReceived: (chunk: Partial<any>) => {
          console.log("📦 Chunk received:", chunk);

          // Only update state for meaningful chunks with complete data
          if (
            !chunk ||
            Object.keys(chunk).length === 0 ||
            // Skip updates for single small properties to reduce render cycles
            (Object.keys(chunk).length === 1 &&
              !chunk.response_type &&
              !chunk.answer &&
              !chunk.english_answer)
          ) {
            console.log("⏭️ Skipping empty/incomplete chunk");
            return;
          }

          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }

          console.log("🔄 Processing meaningful chunk:", chunk);

          // Batched update for the message
          setMessages((prev) => {
            const lastMessage = prev.find(
              (msg) => msg.id === assistantPlaceholderId
            );

            if (!lastMessage) {
              console.warn(
                "⚠️ No message found with ID:",
                assistantPlaceholderId
              );
              return prev;
            }

            console.log("📝 Current message before update:", lastMessage);

            // Handle buddy recommendation response type
            if (chunk.response_type === "buddy_recommendation") {
              console.log("🤖 Processing buddy recommendation:", chunk);

              const updatedMetadata = {
                ...lastMessage.metadata,
                ...chunk,
                isSearching: false,
                isStreaming: false,
                streamComplete: true,
                function_calls: lastMessage.metadata?.function_calls,
              };

              // For buddy recommendation, show the recommendation message
              const displayText =
                chunk.message || "I have a buddy recommendation for you!";

              console.log("✅ Buddy recommendation display text:", displayText);

              return prev.map((msg) =>
                msg.id === assistantPlaceholderId
                  ? {
                      ...msg,
                      text: displayText,
                      metadata: updatedMetadata,
                      response_type: chunk.response_type,
                      recommended_buddy: chunk.recommended_buddy,
                      buddy_message: chunk.message,
                      buddy_explanation: chunk.explanation,
                      buddy_suggestion: chunk.suggestion,
                      switch_prompt: chunk.switch_prompt,
                      query_classification: chunk.query_classification,
                      current_buddy: chunk.current_buddy,
                    }
                  : msg
              );
            }

            // For text chunks, handle different response types
            if (chunk.response_type) {
              console.log(
                "📝 Processing response with type:",
                chunk.response_type
              );

              // Build updated metadata
              const updatedMetadata = {
                ...lastMessage.metadata,
                ...chunk,
                isSearching: false,
                isStreaming: false,
                streamComplete: true,
                // Don't overwrite function_calls if they exist
                function_calls: lastMessage.metadata?.function_calls,
              };

              // Determine which text to display based on response type and language
              let displayText = lastMessage.text;

              if (chunk.response_type === "learning_path") {
                displayText =
                  "I've created a personalized learning path for you. Follow these steps to master this topic.";
              } else if (
                chunk.english_answer ||
                chunk.hinglish_answer ||
                chunk.answer
              ) {
                // Use the appropriate language or fallback to answer
                if (chunk.detected_language === "english") {
                  displayText = chunk.english_answer || displayText;
                } else {
                  displayText = chunk.hinglish_answer || displayText;
                }

                if (!displayText || displayText === "...") {
                  displayText =
                    chunk.answer || "I'm processing your request...";
                }
              }

              console.log(
                "✅ Final display text:",
                displayText.substring(0, 100) + "..."
              );

              return prev.map((msg) =>
                msg.id === assistantPlaceholderId
                  ? {
                      ...msg,
                      text: displayText,
                      metadata: updatedMetadata,
                      response_type: chunk.response_type,
                    }
                  : msg
              );
            }

            // For chunks that update specific fields
            if (chunk.english_answer || chunk.hinglish_answer || chunk.answer) {
              console.log("📝 Processing answer chunk:", {
                english_answer: chunk.english_answer?.substring(0, 50) + "...",
                hinglish_answer:
                  chunk.hinglish_answer?.substring(0, 50) + "...",
                answer: chunk.answer?.substring(0, 50) + "...",
              });

              let displayText = lastMessage.text;

              // Determine which text to show based on language preference
              const detectedLanguage =
                lastMessage.metadata?.detected_language || "english";

              if (detectedLanguage === "english" && chunk.english_answer) {
                displayText = chunk.english_answer;
              } else if (chunk.hinglish_answer) {
                displayText = chunk.hinglish_answer;
              } else if (chunk.answer) {
                displayText = chunk.answer;
              }

              // Only update if we have new text content
              if (displayText && displayText !== "...") {
                console.log(
                  "✅ Updating with new answer text:",
                  displayText.substring(0, 100) + "..."
                );

                return prev.map((msg) =>
                  msg.id === assistantPlaceholderId
                    ? {
                        ...msg,
                        text: displayText,
                        metadata: {
                          ...msg.metadata,
                          ...chunk,
                        },
                      }
                    : msg
                );
              }
            }

            // For other updates like suggested_follow_ups or learning_path data
            console.log("📝 Processing metadata-only chunk");
            return prev.map((msg) =>
              msg.id === assistantPlaceholderId
                ? {
                    ...msg,
                    metadata: {
                      ...msg.metadata,
                      ...chunk,
                    },
                  }
                : msg
            );
          });
        },

        onComplete: async (response: any) => {
          console.log("🎉 Streaming complete, final response:", response);

          // Clear timeout since we got a complete response
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }

          // Check for tool calls in the response
          const hasTool =
            response.tool_call && typeof response.tool_call === "string";

          // Check for function_calls array from new buddy services
          const hasFunctionCalls =
            response.function_calls &&
            Array.isArray(response.function_calls) &&
            response.function_calls.length > 0;

          if (response.conversation_history) {
            console.log(
              "💾 Updating conversation history:",
              response.conversation_history.length,
              "messages"
            );
            setConversationHistory(response.conversation_history);
          }

          // Handle buddy recommendation response type
          if (response.response_type === "buddy_recommendation") {
            console.log("🤖 Final buddy recommendation processing:", response);

            setMessages((prev) => {
              const lastMessage = prev.find(
                (msg) => msg.id === assistantPlaceholderId
              );

              if (!lastMessage) return prev;

              const displayText =
                response.message || "I have a buddy recommendation for you!";

              console.log(
                "✅ Final buddy recommendation display:",
                displayText
              );

              return prev.map((msg) =>
                msg.id === assistantPlaceholderId
                  ? {
                      ...msg,
                      text: displayText,
                      metadata: {
                        ...msg.metadata,
                        ...response,
                        isStreaming: false,
                        streamComplete: true,
                        processingTool: false,
                      },
                      response_type: response.response_type,
                      recommended_buddy: response.recommended_buddy,
                      buddy_message: response.message,
                      buddy_explanation: response.explanation,
                      buddy_suggestion: response.suggestion,
                      switch_prompt: response.switch_prompt,
                      query_classification: response.query_classification,
                      current_buddy: response.current_buddy,
                      buddy_used: response.buddy_used,
                    }
                  : msg
              );
            });

            // Don't fetch additional resources for buddy recommendations
            return;
          }

          // Transform the streaming message to its final form
          setMessages((prev) => {
            const lastMessage = prev.find(
              (msg) => msg.id === assistantPlaceholderId
            );

            if (!lastMessage) {
              console.warn("⚠️ No message found for final update");
              return prev;
            }

            // Determine the display text based on response type and language
            let displayText = lastMessage.text;

            if (response.response_type === "learning_path") {
              displayText =
                "I've created a personalized learning path for you. Follow these steps to master this topic.";
            } else {
              const detectedLanguage = response.detected_language || "english";

              if (detectedLanguage === "english") {
                displayText = response.english_answer || displayText;
              } else {
                displayText = response.hinglish_answer || displayText;
              }
            }

            console.log("✅ Final message update:", {
              response_type: response.response_type,
              displayText: displayText?.substring(0, 100) + "...",
              hasTool,
              hasFunctionCalls,
            });

            // Process any tool calls
            if (hasTool) {
              console.log(
                "🔧 Tool call detected in response:",
                response.tool_call
              );
              // Process tool call later
              setTimeout(() => {
                try {
                  processToolCalls(response.tool_call);
                } catch (error) {
                  console.error("Error processing tool call:", error);
                }
              }, 100);
            }

            // Process function_calls array from new buddy services
            if (hasFunctionCalls) {
              console.log(
                "🔧 Function calls detected in response:",
                response.function_calls
              );

              // Process each function call
              setTimeout(() => {
                response.function_calls.forEach(
                  async (functionCall: any, index: number) => {
                    try {
                      console.log(
                        `🔧 Executing function call ${index + 1}:`,
                        functionCall.name,
                        functionCall.arguments
                      );

                      // Execute the function call using the global executeAssistantTool
                      const result = await window.executeAssistantTool?.(
                        functionCall.name,
                        functionCall.arguments
                      );

                      console.log(
                        `✅ Function call ${index + 1} result:`,
                        result
                      );

                      // Update message with function call results
                      setMessages((prev) =>
                        prev.map((msg) =>
                          msg.id === assistantPlaceholderId
                            ? {
                                ...msg,
                                tool_execution_results: result,
                                metadata: {
                                  ...msg.metadata,
                                  processingTool: false,
                                  function_calls: [
                                    {
                                      ...functionCall,
                                      result: result,
                                    },
                                  ],
                                },
                              }
                            : msg
                        )
                      );
                    } catch (error) {
                      console.error(
                        `❌ Error executing function call ${index + 1}:`,
                        error
                      );

                      // Update message with error
                      setMessages((prev) =>
                        prev.map((msg) =>
                          msg.id === assistantPlaceholderId
                            ? {
                                ...msg,
                                tool_execution_error: String(error),
                                metadata: {
                                  ...msg.metadata,
                                  processingTool: false,
                                  function_calls: [
                                    {
                                      ...functionCall,
                                      error: String(error),
                                    },
                                  ],
                                },
                              }
                            : msg
                        )
                      );
                    }
                  }
                );
              }, 100);
            }

            // Return the updated message
            return prev.map((msg) =>
              msg.id === assistantPlaceholderId
                ? {
                    ...msg,
                    text: displayText,
                    metadata: {
                      ...msg.metadata,
                      ...response,
                      isStreaming: false,
                      streamComplete: true,
                      // Set processingTool if we have a tool call or function calls
                      processingTool: hasTool || hasFunctionCalls,
                    },
                    // Include other properties from the response
                    response_type: response.response_type,
                    suggested_follow_ups: response.suggested_follow_ups,
                    learning_path: response.learning_path,
                    detected_subject: response.detected_subject,
                    detected_chapter: response.detected_chapter,
                    detected_grade: response.detected_grade,
                    suggested_lectures: response.suggested_lectures,
                    buddy_used: response.buddy_used,
                  }
                : msg
            );
          });

          // Fetch resources for any response that has english_answer (but not for buddy recommendations)
          if (
            response.english_answer &&
            response.response_type === "academic_within_scope" &&
            response.response_type !== "buddy_recommendation"
          ) {
            setTimeout(() => {
              try {
                // Store the query for retrying resources
                setLastQuery(query);

                // Set loading state to true before fetching recommendations
                setIsRecommendationsLoading(true);

                // Create a new AbortController for recommendation fetch
                if (!abortControllerRef.current) {
                  abortControllerRef.current = new AbortController();
                }
                const signal = abortControllerRef.current.signal;

                // Fetch recommendations for the response
                lectureRecommendationMutate({
                  query: {
                    question: query,
                    answer: response.english_answer,
                  },
                  signal,
                })
                  .then(async (recommendationResponse) => {
                    if (signal.aborted) return;

                    // Process recommendations and update the message with resources
                    const recommendations =
                      recommendationResponse.data.results.map((result) => ({
                        id: result.id,
                        title: result.title,
                        path: result.path,
                        youtube_url: result.youtube_url,
                        score: result.score,
                        type: result.type,
                      }));

                    // Create resources from recommendations
                    const resources = recommendations
                      .filter((rec) => rec.score && rec.score > 0.7)
                      .slice(0, 5)
                      .map((rec) => ({
                        title: rec.title,
                        url: `/auth_classroom?subject=${rec.id
                          .split("_")
                          .pop()}&chapter=${rec.path.split("/").pop()}`,
                        type: rec.type || "video",
                        description: `From ${
                          rec.id.split("_")[3] || "PuStack"
                        } course`,
                        thumbnail: rec.youtube_url
                          ? `https://img.youtube.com/vi/${getYouTubeVideoId(
                              rec.youtube_url
                            )}/mqdefault.jpg`
                          : undefined,
                      }));

                    // Add additional web resources if we don't have enough
                    if (
                      response.response_type === "academic_within_scope" &&
                      resources.length < 3
                    ) {
                      try {
                        const webResources = [
                          {
                            title:
                              "Understanding " +
                              query.split(" ").slice(0, 3).join(" "),
                            url:
                              "https://pustack.com/resources/" +
                              query.split(" ")[0].toLowerCase(),
                            type: "article",
                            description:
                              "Explore this topic in depth with our comprehensive guide",
                            thumbnail: undefined,
                          },
                          {
                            title:
                              "Interactive Examples: " + query.split(" ")[0],
                            url:
                              "https://pustack.com/interactive/" +
                              query.split(" ")[0].toLowerCase(),
                            type: "tool",
                            description: "Practice with interactive examples",
                            thumbnail: undefined,
                          },
                        ];
                        resources.push(...webResources);
                      } catch (error) {
                        console.error("Error adding web resources:", error);
                      }
                    }

                    // Fetch relevant live classes (past, present, future)
                    const fetchLiveClasses = async () => {
                      try {
                        // Use the same abort controller as for recommendations
                        if (!abortControllerRef.current) {
                          abortControllerRef.current = new AbortController();
                        }
                        const signal = abortControllerRef.current.signal;

                        // Extract subject from response if available
                        const detectedSubject =
                          response.detected_subject ||
                          (recommendations.length > 0
                            ? recommendations[0].id.split("_").pop()
                            : null);

                        // Get user grade and pro status from UserContext instead of localStorage
                        const userGrade = user?.grade || "class_9";

                        // Call the actual live classes API endpoint
                        const classesResponse = await axios.post(
                          "/api/live-classes",
                          {
                            grade: userGrade,
                            is_pro_user: isUserProTier,
                            filters: {
                              // subject_filter: detectedSubject,
                              query: query, // Use the user's query for semantic search
                              // Don't set time filters to get both upcoming and past classes
                            },
                          },
                          { signal }
                        );

                        return classesResponse.data.live_classes || [];
                      } catch (error) {
                        // Check if this is an abort error
                        if (axios.isCancel(error)) {
                          console.log("Live classes fetch aborted");
                          return [];
                        }
                        console.error("Error fetching live classes:", error);
                        return [];
                      }
                    };

                    // Fetch Blaze sessions relevant to the user's query
                    const fetchBlazeSessions = async () => {
                      try {
                        // Use the same abort controller
                        if (!abortControllerRef.current) {
                          abortControllerRef.current = new AbortController();
                        }
                        const signal = abortControllerRef.current.signal;

                        // Get user ID for fetching student sessions
                        const userId = user?.uid || null;

                        // Don't fetch if no user is logged in
                        if (!userId) {
                          console.log(
                            "No user ID available, skipping Blaze sessions fetch"
                          );
                          return [];
                        }

                        // Call the Blaze sessions API endpoint
                        const blazeResponse = await axios.post(
                          "https://vector-search-python-fast-661886367826.asia-south1.run.app/search-blaze-sessions", // Use the correct endpoint
                          {
                            student_id: userId, // REQUIRED: The student's ID
                            query: query, // The semantic search query
                            filters: {
                              // Optional filters can be added here
                              // skill: response.detected_subject,
                            },
                            size: 4, // Limit to 4 sessions
                            min_score: 0.675, // Default minimum score threshold
                          },
                          { signal }
                        );

                        console.log(
                          "Fetched Blaze sessions:",
                          blazeResponse.data
                        );
                        return blazeResponse.data.results || [];
                      } catch (error) {
                        // Check if this is an abort error
                        if (axios.isCancel(error)) {
                          console.log("Blaze sessions fetch aborted");
                          return [];
                        }
                        console.error("Error fetching Blaze sessions:", error);
                        return [];
                      }
                    };

                    // Fetch both live classes and Blaze sessions concurrently
                    const [liveClasses, blazeSessions] = await Promise.all([
                      fetchLiveClasses(),
                      fetchBlazeSessions(),
                    ]);

                    // Update the message with recommendations, resources, live classes, and Blaze sessions
                    setMessages((prev) =>
                      prev.map((msg) =>
                        msg.id === assistantPlaceholderId
                          ? {
                              ...msg,
                              metadata: {
                                ...msg.metadata,
                                recommendations,
                              },
                              resources,
                              live_classes:
                                liveClasses.length > 0
                                  ? liveClasses
                                  : undefined,
                              blaze_sessions:
                                blazeSessions.length > 0
                                  ? blazeSessions
                                  : undefined,
                            }
                          : msg
                      )
                    );
                  })
                  .catch((error) => {
                    console.error("Error fetching recommendations:", error);
                  })
                  .finally(() => {
                    // Set loading state to false when all done
                    setIsRecommendationsLoading(false);
                  });
              } catch (error) {
                console.error("Error setting up recommendation fetch:", error);
                // Set loading state to false in case of setup error
                setIsRecommendationsLoading(false);
              }
            }, 300); // Small delay to ensure UI updates first
          }
        },

        onError: (error: Error) => {
          console.error("🚨 Streaming error:", error);

          // Clear timeout
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }

          // Update the message with error state
          setMessages((prev) => {
            const lastMessage = prev.find(
              (msg) => msg.id === assistantPlaceholderId
            );

            if (!lastMessage) return prev;

            const errorMessage =
              error.message ||
              "I encountered an error processing your request. Please try again.";

            console.log("❌ Setting error message:", errorMessage);

            return prev.map((msg) =>
              msg.id === assistantPlaceholderId
                ? {
                    ...msg,
                    text: errorMessage,
                    metadata: {
                      ...msg.metadata,
                      isStreaming: false,
                      streamComplete: true,
                      error_type: "streaming_error",
                      original_error: error.message,
                    },
                  }
                : msg
            );
          });
        },
      };

      // Check if lecture history is available and not empty
      const userLectureHistory =
        Array.isArray(lectureHistory) && lectureHistory.length > 0
          ? lectureHistory
          : undefined;

      // Log the lecture history status
      if (userLectureHistory) {
        console.log(
          `Sending ${userLectureHistory.length} lecture history items to Gemini`
        );
      } else {
        console.log("No lecture history available to send to Gemini");
      }

      // First, get answer from OpenAI/Gemini with streaming
      await blazeQuery.mutateAsync({
        query,
        syllabus: "NCERT", // Default syllabus
        grade: getClassName(user?.grade) || "Class 10", // Default grade
        signal, // Pass the abort signal to the mutation
        conversation_history: currentHistory, // Pass the conversation history
        streamCallbacks, // Pass callbacks for streaming
        user_lecture_history: userLectureHistory, // Only pass lecture history if available
        selected_buddy: selectedBuddyId || "explainer", // Pass the selected buddy to the API (now defaults to explainer)
      });
    } catch (error) {
      // Clear timeout if it exists
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      // Check if this was an abort error - no need to show an error message
      if (error.name === "AbortError") {
        return;
      }

      // Get the error message from the error object
      let errorMessageText = "";

      // Safely extract error message, ensuring it's always a string
      if (error && typeof error.message === "string") {
        errorMessageText = error.message;
      } else if (error && error.message) {
        // If error.message exists but is not a string, convert it to string
        errorMessageText = String(error.message);
      } else if (error && typeof error === "string") {
        // If error itself is a string
        errorMessageText = error;
      } else {
        // Fallback message
        errorMessageText =
          "I'm sorry, I encountered an error processing your request. Please try again.";
      }

      let errorType = "general";

      // Check for HTTP errors like 400, 404, 500, etc.
      if (errorMessageText && errorMessageText.includes("HTTP error")) {
        console.error("HTTP error detected:", errorMessageText);

        // Special handling for HTTP error 400
        if (errorMessageText.includes("400")) {
          errorMessageText =
            "I'm sorry, I had trouble understanding your request. Please try asking in a different way.";
          errorType = "bad_request";
        } else {
          errorMessageText =
            "I'm sorry, I encountered an error connecting to the server. Please try again later.";
          errorType = "http_error";
        }
      }
      // Handle SyntaxError separately (usually from JSON parsing)
      else if (error instanceof SyntaxError) {
        console.error("JSON parsing error:", error);
        errorMessageText =
          "I'm having trouble understanding the response from our servers. Please try your question again.";
        errorType = "parsing_error";
      }
      // Check for specific error types
      else if (errorMessageText) {
        // Try to parse error message if it's in JSON format
        try {
          // Some errors might be returned as stringified JSON
          if (
            errorMessageText.includes("{") &&
            errorMessageText.includes("}")
          ) {
            const errorJson = JSON.parse(
              errorMessageText.substring(
                errorMessageText.indexOf("{"),
                errorMessageText.lastIndexOf("}") + 1
              )
            );
            if (errorJson.error) {
              // Ensure the error is always a string
              if (typeof errorJson.error === "string") {
                errorMessageText = errorJson.error;
              } else {
                errorMessageText = String(errorJson.error);
              }
            }
          }
        } catch (parseError) {
          // If parsing fails, continue with the original error message
          console.log("Error parsing error message:", parseError);
        }

        // Handle specific error messages
        if (
          errorMessageText.includes(
            "I understand you're curious about this topic"
          ) ||
          errorMessageText.includes(
            "Query is not related to the syllabus or grade level"
          )
        ) {
          // Extract any subject, chapter, or grade information if available
          let detectedSubject = "Mathematics";
          let detectedChapter = "Advanced Topic";
          let detectedGrade = "Beyond Class 10";

          // Try to parse error message to extract more info if possible
          try {
            if (
              errorMessageText.includes("{") &&
              errorMessageText.includes("}")
            ) {
              const errorJson = JSON.parse(
                errorMessageText.substring(
                  errorMessageText.indexOf("{"),
                  errorMessageText.lastIndexOf("}") + 1
                )
              );
              if (errorJson.question_answer) {
                detectedSubject =
                  errorJson.question_answer.detected_subject || detectedSubject;
                detectedChapter =
                  errorJson.question_answer.detected_chapter || detectedChapter;
                detectedGrade =
                  errorJson.question_answer.detected_grade || detectedGrade;

                // If we have an answer from the API, use it
                if (errorJson.question_answer.answer) {
                  // Ensure the answer is always a string
                  if (typeof errorJson.question_answer.answer === "string") {
                    errorMessageText = errorJson.question_answer.answer;
                  } else {
                    errorMessageText = String(errorJson.question_answer.answer);
                  }
                }
              }
            }
          } catch (parseError) {
            console.log("Error parsing out of scope response:", parseError);
          }

          // Make sure we have a default message if extraction failed
          if (
            !errorMessageText ||
            errorMessageText ===
              "I'm sorry, I encountered an error processing your request. Please try again."
          ) {
            errorMessageText =
              "I understand you're curious about this topic, but it seems to be beyond the current syllabus for grades 6-10. I'm designed to help with topics from NCERT syllabus for Classes 6, 7, 8, 9, and 10. Could you ask something from your grade level? I'd be happy to explain concepts from Mathematics, Science, Social Studies, or other subjects within your curriculum!";
          }

          errorType = "academic_out_of_scope";

          // Update the placeholder message instead of creating a new one
          setMessages((prev) => {
            const placeholderIndex = prev.findIndex(
              (msg) => msg.metadata?.isStreaming === true
            );

            if (placeholderIndex !== -1) {
              return prev.map((msg, index) =>
                index === placeholderIndex
                  ? {
                      ...msg,
                      text: errorMessageText,
                      metadata: {
                        error_type: errorType,
                        original_error: "",
                        detected_subject: detectedSubject,
                        detected_chapter: detectedChapter,
                        detected_grade: detectedGrade,
                        response_type: "academic_out_of_scope",
                        isStreaming: false,
                        selectedBuddyId: selectedBuddyId,
                      },
                    }
                  : msg
              );
            }

            // Only create a new message if we can't find one to update
            const errorMessage: Message = {
              id: `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
              text: errorMessageText,
              sender: "assistant",
              timestamp: new Date().toISOString(),
              metadata: {
                error_type: errorType,
                original_error: "",
                detected_subject: detectedSubject,
                detected_chapter: detectedChapter,
                detected_grade: detectedGrade,
                response_type: "academic_out_of_scope",
                isStreaming: false,
                selectedBuddyId: selectedBuddyId,
              },
            };
            return [...prev, errorMessage];
          });

          // Return early since we've already added the message
          return;
        } else if (
          errorMessageText.includes(
            "Please ask academic questions related to your syllabus"
          )
        ) {
          errorMessageText =
            "I'm here to help with academic questions. Could you please ask something related to your studies or schoolwork?";
          errorType = "non_academic";
        } else if (errorMessageText.includes("Invalid origin or token")) {
          errorMessageText =
            "There seems to be an authentication issue. Please try refreshing the page or contact support if the problem persists.";
          errorType = "authentication";
        } else if (
          errorMessageText.includes("network") ||
          errorMessageText.includes("timeout") ||
          errorMessageText.includes("Network Error")
        ) {
          errorMessageText =
            "I'm having trouble connecting to the server. Please check your internet connection and try again.";
          errorType = "network";
        } else if (
          errorMessageText.includes("rate limit") ||
          errorMessageText.includes("too many requests")
        ) {
          errorMessageText =
            "It looks like we're experiencing high demand right now. Please try again in a moment.";
          errorType = "rate_limit";
        }
      }

      // Find and update the placeholder message instead of creating a new one
      setMessages((prev) => {
        const placeholderIndex = prev.findIndex(
          (msg) => msg.metadata?.isStreaming === true
        );

        if (placeholderIndex !== -1) {
          return prev.map((msg, index) =>
            index === placeholderIndex
              ? {
                  ...msg,
                  text: errorMessageText,
                  metadata: {
                    error_type: errorType,
                    original_error: "",
                    isStreaming: false,
                    selectedBuddyId: selectedBuddyId,
                  },
                }
              : msg
          );
        }

        // Only create a new message if we can't find one to update
        const errorMessage: Message = {
          id: `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
          text: errorMessageText,
          sender: "assistant",
          timestamp: new Date().toISOString(),
          metadata: {
            error_type: errorType,
            original_error: "",
            isStreaming: false,
            selectedBuddyId: selectedBuddyId,
          },
        };
        return [...prev, errorMessage];
      });
    } finally {
      // Clear the AbortController if it hasn't been cleared already
      abortControllerRef.current = null;

      // Clear timeout if it's still active
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  };

  // Helper function to fetch recommendations
  const fetchRecommendations = async (
    question: string,
    answer: string,
    messageId: string
  ) => {
    try {
      // Set loading state to true at the start
      setIsRecommendationsLoading(true);

      if (!abortControllerRef.current) {
        abortControllerRef.current = new AbortController();
      }

      const signal = abortControllerRef.current.signal;

      // Now get lecture recommendations based on the question and answer
      const recommendationResponse = await lectureRecommendationMutate({
        query: {
          question,
          answer,
        },
        signal, // Pass the abort signal to the recommendation mutation
      });

      // If the request was aborted, don't continue processing
      if (signal.aborted) return;

      // Extract recommendations
      const recommendations = recommendationResponse.data.results.map(
        (result) => ({
          id: result.id,
          title: result.title,
          path: result.path,
          youtube_url: result.youtube_url,
          score: result.score,
          type: result.type,
        })
      );

      // Create resources array from recommendations for the ResourcesSection
      const resources = recommendations
        .filter((rec) => rec.score && rec.score > 0.7) // Only use high-quality matches
        .slice(0, 5) // Limit to 5 top resources
        .map((rec) => ({
          title: rec.title,
          url: `/auth_classroom?subject=${rec.id
            .split("_")
            .pop()}&chapter=${rec.path.split("/").pop()}`,
          type: rec.type || "video", // Default to video if type is not available
          description: `From ${rec.id.split("_")[3] || "PuStack"} course`,
          thumbnail: rec.youtube_url
            ? `https://img.youtube.com/vi/${getYouTubeVideoId(
                rec.youtube_url
              )}/mqdefault.jpg`
            : undefined,
        }));

      // Get the message to check if this is an academic within scope response
      const messageToUpdate = messages.find((msg) => msg.id === messageId);
      const isAcademicWithinScope =
        messageToUpdate?.metadata?.response_type === "academic_within_scope";

      // Get additional web resources if appropriate
      if (isAcademicWithinScope && resources.length < 3) {
        try {
          // This would be a call to an external API to get web resources
          // For this example, we'll add some sample web resources
          const webResources = [
            {
              title:
                "Understanding " + question.split(" ").slice(0, 3).join(" "),
              url:
                "https://pustack.com/resources/" +
                question.split(" ")[0].toLowerCase(),
              type: "article",
              description:
                "Explore this topic in depth with our comprehensive guide",
              thumbnail: undefined,
            },
            {
              title: "Interactive Examples: " + question.split(" ")[0],
              url:
                "https://pustack.com/interactive/" +
                question.split(" ")[0].toLowerCase(),
              type: "tool",
              description: "Practice with interactive examples",
              thumbnail: undefined,
            },
          ];

          // Add these to our resources
          resources.push(...webResources);
        } catch (error) {
          console.error("Error fetching web resources:", error);
        }
      }

      // Update the message with recommendations and resources
      if (recommendations.length > 0 || resources.length > 0) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  metadata: {
                    ...msg.metadata,
                    recommendations,
                  },
                  resources: resources.length > 0 ? resources : undefined,
                }
              : msg
          )
        );
      }

      // Set loading state to false after successful completion
      setIsRecommendationsLoading(false);
    } catch (error) {
      console.error("Error fetching recommendations:", error);
      // Set loading state to false in case of error
      setIsRecommendationsLoading(false);
    }
  };

  // Add assistant response
  const addAssistantResponse = useMutation({
    mutationFn: async (input: string | Partial<Message>) => {
      console.log("🔔 addAssistantResponse called with:", input);

      // Generate a unique ID for this message
      const messageId = `ai_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 9)}`;

      // Get the selectedBuddyId from input if it's an object
      let buddyId: string | null = null;
      if (typeof input !== "string" && input.metadata?.selectedBuddyId) {
        buddyId = input.metadata.selectedBuddyId;
      }

      // Create new assistant message
      let newMessage: Message;

      if (typeof input === "string") {
        console.log("📝 Processing string input:", input);
        newMessage = {
          id: messageId,
          text: input,
          sender: "assistant",
          timestamp: new Date(),
          metadata: {
            isStreaming: false,
            streamComplete: true,
          },
        };
      } else {
        console.log("📦 Processing object input:", input);
        newMessage = {
          id: `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
          text: input.text || "",
          sender: "assistant",
          timestamp: new Date().toISOString(),
          messageType: input.messageType,
          metadata: input.metadata,
        };

        // Handle blazeSessionConfirmation messages directly without AI processing
        if (input.messageType === "blazeSessionConfirmation") {
          console.log(
            "🎯 Handling blazeSessionConfirmation directly - skipping AI processing"
          );

          // Add to local state immediately and return
          setMessages((prev) => {
            console.log("✅ Adding blazeSessionConfirmation message to state");
            return [...prev, newMessage];
          });

          return { success: true, messageId: newMessage.id };
        }

        // Add to local state for other message types
        setMessages((prev) => [...prev, newMessage]);
        return { success: true, messageId: newMessage.id };
      }

      // Add simple greeting messages directly without OpenAI call
      if (
        input === "What would you like to know today?" ||
        input === "Great! I'm glad I could help" ||
        (typeof input === "string" &&
          (input.includes("Would you like to connect with a real tutor") ||
            input.includes("Welcome back")))
      ) {
        console.log("🎉 Adding simple greeting message directly");
        setMessages((prevMessages) => [...prevMessages, newMessage]);
        return { success: true, messageId };
      }

      // Find the last user query if any
      const lastUserMessage = [...messages]
        .reverse()
        .find((msg) => msg.sender === "user");

      if (!lastUserMessage) {
        setMessages((prevMessages) => [...prevMessages, newMessage]);
        return { success: true, messageId };
      }

      // Start tracking the fetch time
      const fetchStartTimeMs = Date.now();
      setFetchStartTime(fetchStartTimeMs);
      setIsFetching(true);
      setRequestCancelled(false);

      // Save the query for potential retries
      setLastQuery(lastUserMessage.text);

      // Create a new AbortController for this request
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      // Check if lecture history is available and not empty
      const userLectureHistory =
        Array.isArray(lectureHistory) && lectureHistory.length > 0
          ? lectureHistory
          : undefined;

      console.log("Processing the message 1493 | ");

      // Create a placeholder message that will be updated
      const placeholderMessage: Message = {
        id: messageId,
        text: "...",
        sender: "assistant",
        timestamp: new Date(),
        metadata: {
          isStreaming: true,
          selectedBuddyId: buddyId,
        },
      };

      // Add the placeholder message to state
      setMessages((prevMessages) => [...prevMessages, placeholderMessage]);

      try {
        // First ask OpenAI with the selected buddy context
        const blazeResponse = await blazeQuery.mutateAsync({
          query: lastUserMessage.text,
          syllabus: "NCERT", // Default syllabus
          grade: getClassName(user?.grade) || "Class 10", // Default grade
          conversation_history: conversationHistory,
          signal,
          user_lecture_history: userLectureHistory, // Only pass lecture history if available
          selected_buddy: buddyId || "explainer", // Use the buddyId extracted from input
        });

        if (blazeResponse.data.conversation_history) {
          setConversationHistory(blazeResponse.data.conversation_history);
        }

        // Check if the request was aborted
        if (signal.aborted) {
          throw new Error("Request aborted");
        }

        // Get the response details
        const responseBody = blazeResponse;
        const responseData = responseBody?.data;
        const responseType = responseData?.response_type || "greeting";

        // Prepare metadata based on the type of response
        let metadata: any = {
          response_type: responseType,
          detected_subject: responseData?.detected_subject,
          detected_chapter: responseData?.detected_chapter,
          detected_grade: responseData?.detected_grade,
          detected_language: responseData?.detected_language,
          suggested_follow_ups: responseData?.suggested_follow_ups,
          suggested_lectures: responseData?.suggested_lectures,
        };

        // For learning path responses, include the learning path data
        if (responseType === "learning_path" && responseData?.learning_path) {
          metadata.learning_path = responseData.learning_path;
          // Update the message with the learning path
          newMessage.text =
            "I've created a personalized learning path for you. Follow these steps to master this topic.";
        } else if (responseType === "academic_within_scope") {
          // For academic responses, include hinglish and english answers
          metadata.hinglish_answer = responseData?.hinglish_answer;
          metadata.english_answer = responseData?.english_answer;

          // Use the appropriate language answer based on detected language
          if (
            responseData?.detected_language === "hinglish" ||
            responseData?.detected_language === "hindi"
          ) {
            newMessage.text =
              responseData?.hinglish_answer || "Hinglish answer";
          } else {
            newMessage.text = responseData?.english_answer || "English answer";
          }
        } else {
          // For other response types, use the answer field
          newMessage.text = responseData?.answer || "Answer";
        }

        // Update the placeholder message instead of adding a new one
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg.id === messageId
              ? {
                  ...newMessage,
                  metadata: {
                    ...metadata,
                    isStreaming: false,
                  },
                }
              : msg
          )
        );

        return { success: true, messageId };
      } catch (error) {
        console.error("Error adding assistant response:", error);

        // Update the placeholder message with the error
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  text: "I'm sorry, I encountered an error processing your request. Please try again.",
                  metadata: {
                    error_type: "response_error",
                    original_error: error?.message || "Unknown error occurred",
                    isStreaming: false,
                  },
                }
              : msg
          )
        );

        // Even though we had an error, return the messageId to avoid breaking the UI
        return { success: false, messageId };
      }
    },
  });

  // Function to format messages for the conversation history
  const formatMessagesForHistory = useCallback((messages: Message[]) => {
    // Take the last 5 message pairs (max 10 messages) to avoid token limits
    const recentMessages = messages.slice(-10);

    return recentMessages.map((msg) => {
      // Start with the basic format
      const formattedMsg: any = {
        role: msg.sender === "user" ? "user" : "assistant",
        content: msg.text,
      };

      // For assistant messages, include function calls if they exist
      if (msg.sender === "assistant") {
        // Add function_calls if there are tool_calls
        if (msg.tool_calls && msg.tool_calls.length > 0) {
          formattedMsg.function_calls = msg.tool_calls.map((tool) => ({
            name: tool.name,
            args: tool.arguments,
          }));
        }

        // Also add function_calls if they exist directly in metadata
        if (
          msg.metadata?.function_calls &&
          Array.isArray(msg.metadata.function_calls)
        ) {
          formattedMsg.function_calls = msg.metadata.function_calls.map(
            (fc) => ({
              name: fc.name,
              args: fc.arguments || fc.args,
            })
          );
        }

        // Also add function_calls if they exist in metadata.tool_call string format
        if (msg.metadata?.tool_call) {
          // Extract function call information from the tool_call string
          // Format expected: !tool[[name:param1=value1,param2=value2]]
          const toolCallMatch = msg.metadata.tool_call.match(
            /!tool\[\[(.*?):(.*?)\]\]/
          );

          if (toolCallMatch && toolCallMatch.length >= 3) {
            const toolName = toolCallMatch[1];
            const paramString = toolCallMatch[2];

            // Parse parameters
            const params = paramString.split(",").reduce((acc, param) => {
              const [key, value] = param.split("=");
              if (key && value) {
                acc[key.trim()] = value.trim();
              }
              return acc;
            }, {});

            // Add to function_calls if not already present
            if (!formattedMsg.function_calls) {
              formattedMsg.function_calls = [];
            }

            formattedMsg.function_calls.push({
              name: toolName,
              args: params,
            });
          }
        }
      }

      return formattedMsg;
    });
  }, []);

  // Update conversation history whenever messages change
  // useEffect(() => {
  //   if (messages.length > 0) {
  //     setConversationHistory(formatMessagesForHistory(messages));
  //   }
  // }, [messages, formatMessagesForHistory]);

  // Watch for messages that need automatic blaze sessions fetch
  useEffect(() => {
    const latestMessage = messages[messages.length - 1];

    // Check if the latest message has show_blaze_sessions flag
    if (
      latestMessage?.metadata?.show_blaze_sessions &&
      latestMessage.sender === "assistant" &&
      user?.uid &&
      !latestMessage.blaze_sessions // Only fetch if not already fetched
    ) {
      console.log("🔥 Auto-fetching blaze sessions after session creation");

      // Fetch blaze sessions using the same logic as in the existing fetchBlazeSessions function
      const fetchBlazeSessionsAfterCreation = async () => {
        try {
          const signal = abortControllerRef.current?.signal;

          const blazeResponse = await axios.post(
            "https://vector-search-python-fast-661886367826.asia-south1.run.app/search-blaze-sessions",
            {
              student_id: user.uid,
              query: "my blaze sessions", // Generic query to get all user sessions
              size: 10, // Get more sessions to include the newly created one
              min_score: 0.0, // Lower threshold to ensure we get all sessions
            },
            { signal }
          );

          const blazeSessions = blazeResponse.data.results || [];
          console.log(
            "✅ Fetched blaze sessions after creation:",
            blazeSessions
          );

          // Update the message with blaze sessions
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === latestMessage.id
                ? {
                    ...msg,
                    blaze_sessions: blazeSessions,
                    response_type: "blaze_sessions_list",
                    metadata: {
                      ...msg.metadata,
                      show_blaze_sessions: false, // Prevent refetching
                    },
                  }
                : msg
            )
          );
        } catch (error) {
          console.error(
            "❌ Error fetching blaze sessions after creation:",
            error
          );
        }
      };

      // Add a small delay to ensure the session is properly created in the backend
      setTimeout(fetchBlazeSessionsAfterCreation, 1000);
    }
  }, [messages, user?.uid]);

  return {
    messages,
    isLoading: false,
    addMessage,
    addAssistantResponse,
    isAuthenticated: true, // Always return true since we now support guest users
    blazeQueryLoading: blazeQuery.isPending,
    recommendationsLoading: isRecommendationsLoading, // Use our explicit state instead
    interruptRequest, // Export the interrupt function
    requestCancelled,
    retryResourceFetch,
    conversationHistory, // Export conversation history for debugging
    lectureHistory, // Export lecture history for external use if needed
    isLectureHistoryLoading, // Export loading state for lecture history
    lectureHistoryError, // Export any errors for the lecture history
  };
};
